<!-- Modules & Capabilities Section -->
<section class="py-16 lg:py-24 bg-[#f5f0e8] relative border-t border-[#0e3b2e]/10">
  <div class="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
    
    <!-- Section Header Centered -->
    <div class="text-center max-w-3xl mx-auto mb-16 space-y-4">
      <p class="text-xs font-bold text-[#e6b012]">SYSTEM CAPABILITIES</p>
      <h2 class="text-2xl sm:text-3xl lg:text-4xl font-black text-[#0e3b2e] tracking-tight">
        Everything you need to track, evaluate &amp; learn
      </h2>
      <p class="text-slate-500 text-base lg:text-m">
        Six interconnected components designed specifically for public sector monitoring, evaluation and learning workflows.
      </p>
    </div>

    <!-- 6 Grid Cards -->
    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
      
      <!-- Card 1 -->
      <div class="p-8 rounded-2xl bg-white border border-amber-900/10 shadow-xs hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group">
        <div class="w-12 h-12 rounded-full bg-amber-50 text-[#e6b012] flex items-center justify-center mb-6 group-hover:bg-[#e6b012] group-hover:text-white transition-colors">
          <mat-icon class="!text-2xl">account_tree</mat-icon>
        </div>
        <h3 class="text-xl font-bold text-[#0e3b2e] mb-2">M&amp;E Frameworks</h3>
        <p class="text-slate-600 text-sm leading-relaxed mb-6">
          Standardize indicators, targets, baselines, and outcomes across sectors into unified national and institutional frameworks.
        </p>
        <a routerLink="/frameworks" class="inline-flex items-center gap-1.5 text-xs font-bold text-[#0e3b2e] group-hover:text-[#e6b012] transition-colors">
          <span>Explore Frameworks</span>
          <mat-icon class="!text-[16px] !w-4 !h-4">arrow_forward</mat-icon>
        </a>
      </div>

      <!-- Card 2 -->
      <div class="p-8 rounded-2xl bg-white border border-amber-900/10 shadow-xs hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group">
        <div class="w-12 h-12 rounded-full bg-amber-50 text-[#e6b012] flex items-center justify-center mb-6 group-hover:bg-[#e6b012] group-hover:text-white transition-colors">
          <mat-icon class="!text-2xl">event_note</mat-icon>
        </div>
        <h3 class="text-xl font-bold text-[#0e3b2e] mb-2">Strategic Plans</h3>
        <p class="text-slate-600 text-sm leading-relaxed mb-6">
          Align long-term development strategies (Vision 2050, FYDP III) with operational annual workplans and budget execution.
        </p>
        <a routerLink="/mne/strategic-plans" class="inline-flex items-center gap-1.5 text-xs font-bold text-[#0e3b2e] group-hover:text-[#e6b012] transition-colors">
          <span>View Strategic Plans</span>
          <mat-icon class="!text-[16px] !w-4 !h-4">arrow_forward</mat-icon>
        </a>
      </div>

      <!-- Card 3 -->
      <div class="p-8 rounded-2xl bg-white border border-amber-900/10 shadow-xs hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group">
        <div class="w-12 h-12 rounded-full bg-amber-50 text-[#e6b012] flex items-center justify-center mb-6 group-hover:bg-[#e6b012] group-hover:text-white transition-colors">
          <mat-icon class="!text-2xl">business_center</mat-icon>
        </div>
        <h3 class="text-xl font-bold text-[#0e3b2e] mb-2">Projects M&amp;E</h3>
        <p class="text-slate-600 text-sm leading-relaxed mb-6">
          Track infrastructure and capital projects progress, milestones, procurement, and expenditure in real time.
        </p>
        <a routerLink="/projects" class="inline-flex items-center gap-1.5 text-xs font-bold text-[#0e3b2e] group-hover:text-[#e6b012] transition-colors">
          <span>Track Projects</span>
          <mat-icon class="!text-[16px] !w-4 !h-4">arrow_forward</mat-icon>
        </a>
      </div>

      <!-- Card 4 -->
      <div class="p-8 rounded-2xl bg-white border border-amber-900/10 shadow-xs hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group">
        <div class="w-12 h-12 rounded-full bg-amber-50 text-[#e6b012] flex items-center justify-center mb-6 group-hover:bg-[#e6b012] group-hover:text-white transition-colors">
          <mat-icon class="!text-2xl">pie_chart</mat-icon>
        </div>
        <h3 class="text-xl font-bold text-[#0e3b2e] mb-2">Sector M&amp;E</h3>
        <p class="text-slate-600 text-sm leading-relaxed mb-6">
          Aggregate health, education, water, agriculture, and energy sector indicators into automated executive dashboards.
        </p>
        <a routerLink="/mne/mne-sector" class="inline-flex items-center gap-1.5 text-xs font-bold text-[#0e3b2e] group-hover:text-[#e6b012] transition-colors">
          <span>Sector Overview</span>
          <mat-icon class="!text-[16px] !w-4 !h-4">arrow_forward</mat-icon>
        </a>
      </div>

      <!-- Card 5 -->
      <div class="p-8 rounded-2xl bg-white border border-amber-900/10 shadow-xs hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group">
        <div class="w-12 h-12 rounded-full bg-amber-50 text-[#e6b012] flex items-center justify-center mb-6 group-hover:bg-[#e6b012] group-hover:text-white transition-colors">
          <mat-icon class="!text-2xl">assignment</mat-icon>
        </div>
        <h3 class="text-xl font-bold text-[#0e3b2e] mb-2">Annual Workplans</h3>
        <p class="text-slate-600 text-sm leading-relaxed mb-6">
          Operationalize targets into quarterly activities with clear institutional assignments and milestone verification.
        </p>
        <a routerLink="/mne/annual-plans" class="inline-flex items-center gap-1.5 text-xs font-bold text-[#0e3b2e] group-hover:text-[#e6b012] transition-colors">
          <span>Annual Workplans</span>
          <mat-icon class="!text-[16px] !w-4 !h-4">arrow_forward</mat-icon>
        </a>
      </div>

      <!-- Card 6 -->
      <div class="p-8 rounded-2xl bg-white border border-amber-900/10 shadow-xs hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group">
        <div class="w-12 h-12 rounded-full bg-amber-50 text-[#e6b012] flex items-center justify-center mb-6 group-hover:bg-[#e6b012] group-hover:text-white transition-colors">
          <mat-icon class="!text-2xl">menu_book</mat-icon>
        </div>
        <h3 class="text-xl font-bold text-[#0e3b2e] mb-2">Institutional Knowledge</h3>
        <p class="text-slate-600 text-sm leading-relaxed mb-6">
          Centralize evaluation reports, audit recommendations, lessons learned, and policy briefs into a searchable library.
        </p>
        <a routerLink="/documents" class="inline-flex items-center gap-1.5 text-xs font-bold text-[#0e3b2e] group-hover:text-[#e6b012] transition-colors">
          <span>Knowledge Library</span>
          <mat-icon class="!text-[16px] !w-4 !h-4">arrow_forward</mat-icon>
        </a>
      </div>

    </div>

  </div>
</section>
